import moment from 'moment/moment'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { ComputeFactory } from '../../models/ComputeFactory'
import { Invoice } from '../../models/Invoice'
import { Line } from '../../models/Line'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import { PDFPreviewModal } from '../common/PDFPreviewModal'
import { InvoiceView } from './InvoiceView'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { NewInvoicePage } from './NewInvoicePage'
import { NewInvoiceCompletePage } from './NewInvoiceCompletePage'

const viewWillAppear = async ({
    owner,
    me,
    urlParams,
    props: { action },
    searchParams,
}: PocketComponent<{ action: 'new' | 'edit' | 'copy' }>) => {
    const [contracts, items] = await Promise.all([
        RecordFactory.getInstance().loadContracts(),
        RecordFactory.getInstance().loadItems(owner.id ?? ''),
    ])

    const filteredItems = items.filter((item) =>
        ['service', 'class', 'reservation', 'product'].includes(item.type ?? '')
    )

    const defaultInvoice = await (async (): Promise<Invoice | null> => {
        switch (action) {
            case 'new': {
                const invoice = new Invoice({
                    type: 'invoice',
                    reminder: owner.invoiceReminder,
                    channel: owner.channel,
                    date: moment().startOf("day"),
                    due_date: moment().startOf("day"),
                    owner: owner,
                    status: 'sentQuote',
                    allow_surcharge: owner.allowSurcharge,
                    vendor: owner,
                })
                invoice.allowAnyPayment = undefined

                const bookingID = searchParams.get('bookingId')
                if (bookingID !== null) {
                    const shouldIncludeSchedule =
                        searchParams.get('shouldIncludeSchedule') === 'true'

                    const booking = await RecordFactory.getInstance().loadBooking(bookingID)
                    if (!booking) {
                        return null
                    }
                    invoice.due_date = booking.endDate!.clone().startOf('day')
                    invoice.client = booking.client
                    invoice.setVendorAndOwner(booking.vendor)
                    invoice.memo =
                        booking.memo && booking.getLocation()
                            ? `${booking.getLocation()}\n${booking.memo}`
                            : (booking.memo ?? booking.getLocation() ?? '')
                    invoice.lines = []
                    if (booking.taxAmount) {
                        invoice.taxcode = booking.taxcode
                        invoice.tax = booking.tax
                        invoice.nexus = booking.nexus
                        if (invoice.nexus === 'transaction') {
                            invoice.location = booking.getLocation()
                        }
                    }
                    invoice.reminder = booking.vendor?.invoiceReminder ?? ''
                    if (shouldIncludeSchedule && booking.schedule) {
                        const scheduleBookings =
                            await ComputeFactory.getInstance().fetchScheduleBookings(
                                booking.schedule
                            )

                        const lines = scheduleBookings.map((b) => Line.initWithBooking(b, invoice))
                        invoice.lines = lines
                        invoice.total = lines.reduce((sum: number, line: Line) => {
                            return sum + (line.total ?? 0)
                        }, 0)
                    }
                    else {
                        const line = Line.initWithBooking(booking, invoice)
                        const owedAmount =
                            !!Number(booking.deposit) && !!booking.paidDepositDate
                                ? Number(booking.total) - Number(booking.deposit)
                                : booking.total

                        if (line) {
                            invoice.lines.push(line)
                        }

                        if (booking.paidDepositDate) {
                            invoice.total = owedAmount
                            invoice.discount = booking.discountAmount
                            invoice.discountType = 'rate'
                            const depositLine = Line.initWithType('expense', invoice)
                            depositLine.taxable = !!Number(booking.taxAmount)
                            depositLine.category = 'adjustments'
                            depositLine.total = -Number(booking.deposit)
                            depositLine.memo = `Deposit - paid on ${moment(booking.paidDepositDate).format('MMM d, yyyy')}`
                            depositLine.date = booking.paidDepositDate
                            invoice.lines.push(depositLine)
                        }
                        else {
                            invoice.total = owedAmount
                            invoice.discount = booking.discount
                            invoice.discountType = booking.discountType
                        }
                    }
                    return invoice
                }

                const estimateID = searchParams.get('estimateId')
                if (estimateID !== null) {
                    await RecordFactory.getInstance().fetchEstimate(estimateID)
                    const estimate = await RecordFactory.getInstance().loadEstimate(estimateID)
                    if (!estimate) {
                        return null
                    }

                    invoice.estimate = estimate
                    invoice.client = estimate.client
                    invoice.subaccount = estimate.subaccount
                    invoice.setVendorAndOwner(estimate.vendor)
                    if (invoice.vendor?.allowStaffing) {
                        invoice.employee = estimate.employee
                    }
                    invoice.total = estimate.rate
                    invoice.allowSurcharge = estimate.allowSurcharge
                    invoice.taxcode = estimate.taxcode
                    invoice.tax = estimate.tax
                    invoice.nexus = estimate.nexus
                    invoice.location = estimate.location
                    invoice.reminder = owner?.invoiceReminder
                    if (estimate.paidDepositDate) {
                        invoice.discount = estimate.discountAmount
                        invoice.discountType = 'rate'
                    }
                    else {
                        invoice.discount = estimate.discount
                        invoice.discountType = estimate.discountType
                    }
                    for (const line of estimate.lines) {
                        const lineCopy = Line.initWithLine(line, invoice)
                        invoice.lines.push(lineCopy)
                    }
                    if (estimate.paidDepositDate && estimate.lines.length) {
                        const depositLine = Line.initWithType('expense', invoice)
                        depositLine.category = 'adjustments'
                        depositLine.total = -(estimate.deposit || 0)
                        depositLine.memo = `Deposit - paid on ${moment(estimate.paidDepositDate).format('MMM d, yyyy')}`
                        depositLine.date = estimate.paidDepositDate
                        invoice.lines.push(depositLine)
                    }

                    return invoice
                }

                invoice.setVendorAndOwner(owner)
                if (
                    owner.allowStaffing &&
                    contracts.length > 0 &&
                    me.payee &&
                    me.perm === User.PERM_ROLE_EMPLOYEE
                ) {
                    invoice.employee = me
                }

                if (!invoice.memo) {
                    invoice.memo = me.invoicePolicy
                }

                return invoice
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                return (await RecordFactory.getInstance().loadInvoice(urlParams.id)) ?? null
            }
            case 'copy': {
                if (!urlParams.id) {
                    return null
                }
                const invoice =
                    (await RecordFactory.getInstance().loadInvoice(urlParams.id)) ?? null
                if (!invoice) {
                    return null
                }
                return invoice.clone({ id: undefined })
            }
        }
    })()
    if (defaultInvoice === null) {
        return new RecordNotFoundError()
    }

    const clientId = searchParams.get('clientId')
    if (!!clientId) {
        const clientUser = await RecordFactory.getInstance().loadUser(clientId)
        if (!clientUser) {
            return new RecordNotFoundError('Client not found')
        }
        defaultInvoice.client = clientUser

        if (clientUser.id) {
            const client = await RecordFactory.getInstance().loadClient(clientUser.id)
            if (client) {
                if (owner.allowDiscount && client.discount && client.discountType === 'percent') {
                    defaultInvoice.discount = client.discount
                    defaultInvoice.discountType = client.discountType
                }
                if (client.notifyType) {
                    defaultInvoice.channel = client.notifyType
                }
            }
        }
    }

    const subaccountId = searchParams.get('subaccountId')
    if (!!subaccountId) {
        const subaccount = await RecordFactory.getInstance().loadSubaccount(subaccountId)
        if (subaccount) {
            defaultInvoice.subaccount = subaccount
            defaultInvoice.client = subaccount.client
        }
    }

    defaultInvoice.memo = searchParams.get('memo') ?? defaultInvoice.memo

    if (defaultInvoice.lines.length > 0) {
        defaultInvoice.syncTotal()
    }

    return { defaultInvoice, items: filteredItems }
}

const NewInvoiceWorkflowRoot = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { defaultInvoice, items } = data

    const isEditing = defaultInvoice.id !== undefined

    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams()

    const stepIndex = useMemo(() => {
        switch (searchParams.get('step')) {
            case 'review':
                return 1
            case 'success':
                return 2
            default:
                return 0
        }
    }, [searchParams])

    const [invoice, setInvoice] = useState(defaultInvoice)

    const [loadingButton, setLoadingButton] = useState<'charge' | 'send' | null>(null)

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

    const onSave = async (shouldCharge: boolean) => {
        if (invoice.client === undefined) {
            return
        }

        setLoadingButton(shouldCharge ? 'charge' : 'send')

        const result = await RecordFactory.getInstance().sendInvoice(invoice, shouldCharge)
        setLoadingButton(null)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setInvoice(result.value)

        if (shouldCharge) {
            navigate(`/charge/invoice/${result.value.id}`)
        }
        else {
            setSearchParams({ step: 'success' }, { replace: true })
        }
    }

    const handleShowPreview = async () => {
        if (invoice.client === undefined) {
            return
        }
        setIsPreviewModalOpen(true)
    }

    const handleInvoiceUpdate = (updatedInvoice: Invoice) => {
        if (updatedInvoice.lines.length > 0) {
            updatedInvoice.lines.forEach((line) => {
                line.trandoc = updatedInvoice
            })
            updatedInvoice.syncTotal()
        }
        setInvoice(updatedInvoice)
    }

    const onBack = useCallback(() => {
        switch (stepIndex) {
            case 0:
                navigate('/dashboards/invoices')
                break
            default: // review
                setSearchParams()
                break
        }
    }, [isEditing, navigate, stepIndex, setSearchParams])

    const canAdvanceToStep2 = invoice.client !== undefined

    WorkflowPage.useNavigationHandlers({
        stepIndex,
        canAdvanceToStep2,
        isEditing,
        recordID: invoice.id,
    })

    const hasUnsavedChanges = stepIndex <= 1 && invoice.hasChangesFrom(data.defaultInvoice)

    return (
        <>
            <WorkflowPage
                title={stepIndex === 0 ? 'Send invoice' : stepIndex === 1 ? 'Review' : undefined}
                backButtonTitle={
                    stepIndex === 1 ? (isEditing ? 'Edit Invoice' : 'Send Invoice') : undefined
                }
                onBack={onBack}
                steps={[isEditing ? 'Edit' : 'Create', 'Review', 'Send']}
                currentStepIndex={stepIndex}
                shouldForceCurrentStepCheckmark={stepIndex === 2}
                onClose={{
                    action: 'go-back',
                    excludePathname: /^\/invoices.*\/(new|edit|copy)/,
                    defaultPathname: '/dashboards/invoices',
                }}
                footerContent={
                    stepIndex === 0 || stepIndex === 1 ? (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {stepIndex === 0 ? (
                                <>
                                    <Button
                                        intent="secondary"
                                        disabled={invoice.client === undefined}
                                        onClick={handleShowPreview}
                                    >
                                        Preview
                                    </Button>

                                    <Button
                                        intent="primary"
                                        disabled={!canAdvanceToStep2}
                                        onClick={() => setSearchParams({ step: 'review' })}
                                    >
                                        Next
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        intent="secondary"
                                        onClick={handleShowPreview}
                                        disabled={invoice.client === undefined}
                                    >
                                        Preview
                                    </Button>

                                    <Button
                                        intent={!!invoice.channel ? 'secondary' : 'primary'}
                                        onClick={() => onSave(true)}
                                        isLoading={loadingButton === 'charge'}
                                    >
                                        {invoice.chargeAmount === 0 ? 'Mark Paid' : 'Charge'}
                                    </Button>

                                    {!!invoice.channel && (
                                        <Button
                                            intent="primary"
                                            onClick={() => onSave(false)}
                                            isLoading={loadingButton === 'send'}
                                        >
                                            Send
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    ) : undefined
                }
                shouldVerticallyCenterContent={stepIndex === 2}
            >
                {stepIndex === 0 ? (
                    <NewInvoicePage
                        invoice={invoice}
                        onUpdateInvoice={handleInvoiceUpdate}
                        owner={owner}
                        items={items}
                    />
                ) : stepIndex === 1 && invoice.client !== undefined ? (
                    <InvoiceView invoice={invoice} isWorkflowReviewStep client={invoice.client} />
                ) : stepIndex === 2 ? (
                    <NewInvoiceCompletePage invoice={invoice} />
                ) : null}
            </WorkflowPage>

            <PDFPreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                endpoint={{ path: '/invoice/print', body: invoice.jsonObject() }}
            />

            <WorkflowPage.NavigationBlocker
                when={hasUnsavedChanges}
                allowNextPaths={[/charge\/invoice\/.*/]}
            />
        </>
    )
}

const render = (props: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    return (
        <WorkflowPage.ResetContainer>
            <NewInvoiceWorkflowRoot {...props} />
        </WorkflowPage.ResetContainer>
    )
}

export const InvoiceWorkflow = usePSComponent(render, viewWillAppear)
